import { JSX, Match, ParentProps, Show, Signal, Switch, children, createEffect, createMemo, createSignal, on } from 'solid-js'
import { LookupValuePair, ViewState, fkInput as api } from 'st-app/api'
import { debounce, nbsp, position } from 'common';
import Flexbox from '../st-flexbox/st-flexbox';
import Icon from '../st-icon/st-icon';
import Button from '../st-button/st-button';
import Dropdown from '../st-dropdown/st-dropdown';
import Combobox from '../st-combobox/st-combobox';
import { useViewContext } from '../st-view/st-view';
import DataStore, { DataStoreContext, requireDataStoreContext } from '../st-data-store/st-data-store';

import styles from './st-fk-input.module.css'
import inputStyles from '../st-input/st-input.module.css'
import btnStyles from '../st-button/st-button.module.css'
import { createContext } from 'vm';
import { FKInputContext, FKInputContextJsx, requireFKInputContext } from './FKInputContext';

export class FKInputProps {
    value?: Signal<string> = createSignal('');
    label?= '';
    initialDisplayValue?= '';
    identity?= '';
    srcName = '';
    viewID?= '';

    suppressDropdown?= false;
    fkView?= '';
}

export default function FKInput(props: ParentProps<FKInputProps>) {
    const ctx = new FKInputContext();
    const dataStore = requireDataStoreContext();
    const viewContext = useViewContext();

    const [search, setSearch] = createSignal('');
    const [value, setValue] = createSignal<LookupValuePair>();
    const [searchResults, setSearchResults] = createSignal<LookupValuePair[]>([]);

    createEffect(on(() => [value()], () => {
        if (props.value) {
            props.value[1](value()?.value ?? '');
        }
    }));

    const sourcePropName = extractSourcePropFromSrcName(props.srcName);
    const sourceGU = createMemo(() => dataStore.get('currentValue', sourcePropName, props.identity));
    const displayValue = createMemo(() => dataStore.get('currentValue', props.srcName, sourceGU() ?? ''));

    const valueClasses = createMemo(() => {
        const res = [styles.value, inputStyles.input];

        if (props.fkView) {
            res.push(btnStyles.link);
        }

        return res.join(' ');
    });

    const find = debounce((s: string) => {
        if (!s) {
            setSearchResults([]);
        }
        else {
            const viewState = viewContext?.viewState();
            if (viewState) {
                viewState.viewGU = props.viewID;
            }

            api.Find(viewContext?.viewState()!, props.srcName, s).then(setSearchResults);
        }
    }, 500);

    const [showDropdown, setShowDropdown] = createSignal(false);

    function clearValue() {
        setValue(undefined);
        setShowDropdown(false);
    }

    const [contRef, setContRef] = createSignal<HTMLDivElement>();
    createEffect(() => find(search()));

    let first = true;
    createEffect(() => {
        const val = value();

        if (!first) {
            dataStore.setCurrentValue(sourcePropName, val?.value);
            dataStore.setCurrentValue(props.srcName, val?.description, val?.value);

            setShowDropdown(false);
        }
        first = false;
    });

    function launch() {
        if (!sourceGU()) {
            setShowDropdown(true);
        }
        else if (!props.fkView) {
            setShowDropdown(true);
        }
        else if (props.fkView) {
        }
    }

    return <FKInputContextJsx.Provider value={ctx} >
        <Switch>
            <Match when={!!props.children}>{props.children}</Match>
            <Match when={!props.children}>
                <Flexbox ref={setContRef} middle dir="horizontal" class={valueClasses()} onClick={launch} style="padding: 2px 6px" >
                    <Flexbox.Item as="button" aria-label={props.label} auto class={`${styles.value} ${btnStyles.btn}`} >
                        {ctx.valueChild() ?? (displayValue() || (!props.label ? nbsp : ''))}
                    </Flexbox.Item>
                    <Show when={!props.suppressDropdown}>
                        <Flexbox.Item class={inputStyles.addon} >
                            <Button type='link' label="Edit" style="padding: 0px" onClick={() => setShowDropdown(true)} >
                                <Icon src="fas fa-link" />
                            </Button>
                        </Flexbox.Item>
                    </Show>
                </Flexbox>
            </Match>
        </Switch>

        <Dropdown open={[showDropdown, setShowDropdown]} placement='bottom-start' target={contRef()} >
            <Dropdown.Content>
                <Combobox value={[value, setValue]} filter={[search, setSearch]} filterIcon="fas fa-search" items={searchResults()} fallback={<Flexbox.Item><i>No Results</i></Flexbox.Item>} >{it =>
                    ctx.rowChild() ?? (it.displayValue ?? it.description)
                }</Combobox>
                <Flexbox dir="horizontal">
                    <Flexbox.Item auto></Flexbox.Item>
                    <Flexbox.Item>
                        <Button type="link" onClick={clearValue}>clear</Button>
                    </Flexbox.Item>
                </Flexbox>
            </Dropdown.Content>
        </Dropdown>
    </FKInputContextJsx.Provider>
}
FKInput.defaultProps = new FKInputProps();

FKInput.InputValue = function FKInputValue(props: ParentProps) {
    const ctx = requireFKInputContext();
    ctx.setValueChild(props.children);

    return <></>;
};

FKInput.Row = function FKInputRow(props: ParentProps) {
    const ctx = requireFKInputContext();
    ctx.setRowChild(props.children);

    return <></>;
}

function extractSourcePropFromSrcName(srcName: string) {
    const idx = srcName.indexOf('-JoinBy');
    const idxProp = srcName.indexOf('-', idx + 1);

    if (idx < 0) {
        console.error(`Unable to determine source name for FK property: ${srcName}`);
    }

    return `${srcName.substring(0, idx)}-${srcName.substring(idx + 7, idxProp)}`;
}
